import { Injectable, Inject, NgZone } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

declare global {
  interface Window {
    $crisp: any[];
    CRISP_WEBSITE_ID: string;
  }
}

export type CrispPageContext = 'interview-otp' | 'test-otp' | 'registration';

@Injectable({ providedIn: 'root' })
export class CrispService {
  private scriptLoaded = false;

  private chatOpenedSubject = new Subject<void>();
  private chatClosedSubject = new Subject<void>();
  chatOpened$ = this.chatOpenedSubject.asObservable();
  chatClosed$ = this.chatClosedSubject.asObservable();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private ngZone: NgZone
  ) {}

  get isEnabled(): boolean {
    return !!environment.CRISP?.WEBSITE_ID;
  }

  load(): void {
    if (this.scriptLoaded || !environment.CRISP?.WEBSITE_ID) return;
    this.scriptLoaded = true;

    window.$crisp = [];
    window.CRISP_WEBSITE_ID = environment.CRISP.WEBSITE_ID;

    window.$crisp.push(['do', 'chat:hide']);
    window.$crisp.push(['config', 'color:theme', ['deep_purple']]);

    setTimeout(() => {
      const script = this.document.createElement('script');
      script.src = 'https://client.crisp.chat/l.js';
      script.async = true;

      script.onload = () => {
        window.$crisp.push(['on', 'chat:opened', () => {
          this.ngZone.run(() => this.chatOpenedSubject.next());
        }]);
        window.$crisp.push(['on', 'chat:closed', () => {
          this.document.body.classList.remove('crisp-is-open');
          window.$crisp.push(['do', 'chat:hide']);
          this.ngZone.run(() => this.chatClosedSubject.next());
        }]);
      };
      script.onerror = () => {
        this.scriptLoaded = false;
      };
      this.document.head.appendChild(script);
    }, 0);
  }

  showBubble(): void {
    window.$crisp?.push(['do', 'chat:show']);
  }

  setUserIdentity(email: string, name?: string): void {
    if (!window.$crisp) return;
    window.$crisp.push(['set', 'user:email', [email]]);
    if (name) {
      window.$crisp.push(['set', 'user:nickname', [name]]);
    }
  }

  setSessionContext(pageType: CrispPageContext, companyName?: string): void {
    if (!window.$crisp) return;
    window.$crisp.push(['set', 'session:segments', [[pageType]]]);

    const data: [string, string][] = [['page_type', pageType]];
    if (companyName) data.push(['company', companyName]);

    window.$crisp.push(['set', 'session:data', [data]]);
  }

  openChat(): void {
    if (!window.$crisp) return;
    this.document.body.classList.add('crisp-is-open');
    window.$crisp.push(['do', 'chat:show']);
    window.$crisp.push(['do', 'chat:open']);
  }

  hideChat(): void {
    this.document.body.classList.remove('crisp-is-open');
    window.$crisp?.push(['do', 'chat:hide']);
  }

  resetSession(): void {
    window.$crisp?.push(['do', 'session:reset']);
  }
}
